import { createContext, useCallback, useContext, useState, lazy, Suspense, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Crown, Sparkles, ShieldCheck, Zap, RefreshCw, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTrialCheckout } from "@/hooks/use-trial-checkout";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const LazyPricing = lazy(() => import("@/components/Pricing"));

interface UpgradeModalContextValue {
  open: (opts?: { featureLabel?: string }) => void;
  close: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null);

export const useUpgradeModal = () => {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) throw new Error("useUpgradeModal must be used within UpgradeModalProvider");
  return ctx;
};

export const UpgradeModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [featureLabel, setFeatureLabel] = useState<string | undefined>();
  const [showAllPlans, setShowAllPlans] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const tt = (it: string, en: string, es: string) =>
    lang === "it" ? it : lang === "es" ? es : en;

  const { loading, retrying, errorMsg, errorRequestId, start } = useTrialCheckout({
    source: "UpgradeModal",
  });

  const open = useCallback((opts?: { featureLabel?: string }) => {
    setFeatureLabel(opts?.featureLabel);
    setShowAllPlans(false);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const handleStart = () => {
    if (!user) {
      navigate("/auth?tab=register");
      return;
    }
    start();
  };

  return (
    <UpgradeModalContext.Provider value={{ open, close }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md sm:max-w-lg p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-br from-accent/15 via-accent/5 to-transparent px-6 pt-6 pb-5 border-b border-border">
            <DialogHeader>
              <div className="w-12 h-12 rounded-2xl bg-accent/20 flex items-center justify-center mb-3">
                <Crown className="w-6 h-6 text-accent" />
              </div>
              <DialogTitle className="text-2xl font-extrabold">
                {featureLabel
                  ? tt(`Sblocca ${featureLabel}`, `Unlock ${featureLabel}`, `Desbloquea ${featureLabel}`)
                  : tt("Sblocca PRO", "Unlock PRO", "Desbloquea PRO")}
              </DialogTitle>
              <DialogDescription className="text-sm">
                {tt(
                  "7 giorni gratis. Disdici quando vuoi. Nessun addebito durante il trial.",
                  "7 days free. Cancel anytime. No charge during the trial.",
                  "7 días gratis. Cancela cuando quieras. Sin cargos durante la prueba."
                )}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 py-5 space-y-4">
            <ul className="space-y-2.5 text-sm">
              {[
                tt("Link illimitati + analytics avanzate", "Unlimited links + advanced analytics", "Enlaces ilimitados + analítica avanzada"),
                tt("Prodotti digitali, membership, booking, tip jar", "Digital products, memberships, booking, tip jar", "Productos digitales, membresía, reservas, propinas"),
                tt("AI Auto-Funnel, automazioni, CRM contatti", "AI Auto-Funnel, automations, contacts CRM", "AI Auto-Funnel, automatizaciones, CRM"),
                tt("Pixel tracking, A/B test, sticky bar, spin wheel", "Pixel tracking, A/B test, sticky bar, spin wheel", "Pixel tracking, A/B test, barra fija, ruleta"),
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-foreground">
                  <Sparkles className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Button
              size="lg"
              className="w-full gap-2 rounded-full bg-gradient-accent text-accent-foreground hover:opacity-90 border-0 font-bold"
              onClick={handleStart}
              disabled={loading}
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {retrying
                    ? tt(`Riprovo (${retrying.attempt}/${retrying.total})…`, `Retrying (${retrying.attempt}/${retrying.total})…`, `Reintentando (${retrying.attempt}/${retrying.total})…`)
                    : tt("Avvio trial…", "Starting trial…", "Iniciando prueba…")}
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  {tt("Inizia 7 giorni gratis", "Start 7 days free", "Comienza 7 días gratis")}
                </>
              )}
            </Button>

            {errorMsg && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p>{errorMsg}</p>
                  {errorRequestId && (
                    <p className="font-mono opacity-70">Request ID: {errorRequestId}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleStart}
                    className="underline font-semibold"
                  >
                    {tt("Riprova checkout", "Retry checkout", "Reintentar checkout")}
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-1">
              <span className="flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-accent" />
                {tt("Pagamenti sicuri", "Secure payments", "Pagos seguros")}
              </span>
              <span>·</span>
              <span>{tt("Disdici in 1 click", "Cancel in 1 click", "Cancela en 1 click")}</span>
            </div>

            <button
              type="button"
              onClick={() => setShowAllPlans((v) => !v)}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground underline pt-1"
            >
              {showAllPlans
                ? tt("Nascondi tutti i piani", "Hide all plans", "Ocultar todos los planes")
                : tt("Vedi tutti i piani", "See all plans", "Ver todos los planes")}
            </button>

            {showAllPlans && (
              <Suspense
                fallback={
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    {tt("Caricamento piani…", "Loading plans…", "Cargando planes…")}
                  </div>
                }
              >
                <div className="-mx-6">
                  <LazyPricing />
                </div>
              </Suspense>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </UpgradeModalContext.Provider>
  );
};